import { EffectReducer } from 'use-effect-reducer'
import {
  LSPAY_ERROR,
  LSPAY_ERROR_MESSAGES,
  SWIPER_ERROR_CODES,
} from '../../helpers/lspayConstants'
import { getEffectForRetry } from './effects'
import {
  RefundEffect,
  RefundEvent,
  RefundState,
  UnrefRefundReason,
} from './types'

export const refundStateReducer: EffectReducer<
  RefundState,
  RefundEvent,
  RefundEffect
> = (state, event, exec) => {
  switch (event.type) {
    case 'COMPLETE_REFUND': {
      const { status } = state
      if (
        status !== 'refundingViaCardNotPresent' &&
        status !== 'persistingCardPresentRefund' &&
        status !== 'performingUnreferencedRefund'
      ) {
        return state
      }

      return {
        transaction: event.transaction,
        status: 'done',
      }
    }
    case 'CARD_NOT_PRESENT_FAIL': {
      if (state.status !== 'refundingViaCardNotPresent') return state

      const { error, registerId } = event
      const { retailerPaymentType } = state

      if (
        error.code ===
          SWIPER_ERROR_CODES.REFUND_AMOUNT_MORE_THAN_ORIGINAL_TRANSACTION &&
        error.fields?.refundable_amount
      ) {
        return {
          status: 'amountExceededPrompt',
          retailerPaymentType,
          amount: event.amount,
          refundableAmount: error.fields.refundable_amount,
          dismissible: true,
        }
      }
      if (error.code === SWIPER_ERROR_CODES.LSPAY_ZERO_DOLLAR_REFUNDABLE) {
        return {
          status: 'unreferencedPrompt',
          retailerPaymentType,
          amount: event.amount,
          unrefReason: UnrefRefundReason.ZERO_DOLLAR_REFUNDABLE,
          dismissible: true,
        }
      }

      if (
        error.code ===
          SWIPER_ERROR_CODES.LSPAY_CARD_NOT_PRESENT_INTERAC_REFUND &&
        error.fields?.charge_id
      ) {
        return {
          status: 'interacPresentPrompt',
          chargeId: error.fields.charge_id,
          retailerPaymentType,
          registerId,
          amount: event.amount,
          dismissible: true,
        }
      }

      return {
        status: 'error',
        errorMessage: LSPAY_ERROR_MESSAGES[LSPAY_ERROR.DEFAULT_REFUND],
        retryable: true,
        dismissible: true,
        previousState: state,
      }
    }
    case 'CONTINUE_WITH_CARD_PRESENT': {
      if (state.status !== 'interacPresentPrompt') {
        return state
      }

      exec({ type: 'fetchPairingEffect' })

      return {
        status: 'fetchingPairing',
        retailerPaymentType: state.retailerPaymentType,
        refundMethod: { type: 'interac', chargeId: state.chargeId },
        amount: state.amount,
      }
    }
    case 'CONTINUE_WITH_UNREFERENCED': {
      if (
        state.status !== 'amountExceededPrompt' &&
        state.status !== 'unreferencedPrompt'
      )
        return state

      exec({ type: 'fetchPairingEffect' })

      return {
        status: 'fetchingPairing',
        retailerPaymentType: state.retailerPaymentType,
        amount: state.amount,
        refundMethod: { type: 'unreferenced' },
      }
    }
    case 'REFUND_ORIGINAL_AMOUNT': {
      if (state.status !== 'amountExceededPrompt') return state

      const { retailerPaymentType } = state

      exec({ type: 'cardNotPresentEffect', overrideAmount: event.amount })

      return {
        status: 'refundingViaCardNotPresent',
        retailerPaymentType,
      }
    }
    case 'PAIRING_OK': {
      if (state.status !== 'fetchingPairing') return state

      exec({
        type: 'connectTerminalEffect',
        isSimulated: event.pairing.isSimulated,
        pairingSN: event.pairing.sn,
        terminalID: event.pairing.terminalID,
        terminalModel: event.pairing.terminalModel,
      })

      return {
        status: 'connectingToTerminal',
        pairing: event.pairing,
        refundMethod: state.refundMethod,
        retailerPaymentType: state.retailerPaymentType,
        amount: state.amount,
      }
    }
    case 'TERMINAL_OK': {
      if (state.status !== 'connectingToTerminal') return state

      const { terminal, terminalMonitoringInstance, currency } = event
      const { retailerPaymentType, refundMethod, amount } = state

      switch (refundMethod.type) {
        case 'interac': {
          const { chargeId } = refundMethod

          exec({
            type: 'cardPresentEffect',
            terminal,
            terminalMonitoringInstance,
            chargeId,
            amount,
            isRetry: false,
          })

          return {
            status: 'refundingViaCardPresent',
            terminal,
            terminalMonitoringInstance,
            chargeId,
            retailerPaymentType,
            refundMethod,
            amount,
          }
        }
        case 'unreferenced':
          // usd unref refund
          if (currency.toLocaleLowerCase() === 'usd') {
            exec({
              type: 'readReusableCardEffect',
              terminal,
              terminalMonitoringInstance,
              isRetry: false,
            })

            return {
              status: 'readingReusableCard',
              refundMethod,
              retailerPaymentType,
              terminal,
              terminalMonitoringInstance,
              amount,
            }
          }

          // international unref refund
          exec({ type: 'createSetupIntentEffect' })

          return {
            status: 'creatingSetupIntent',
            refundMethod,
            retailerPaymentType,
            terminal,
            terminalMonitoringInstance,
            amount,
          }
        default: {
          // This `never` typing will let us know at compile time if we do not handle an existing event type. Neat !
          const _exhaustiveCheck: never = refundMethod
          return _exhaustiveCheck
        }
      }
    }
    case 'CREATE_SETUP_INTENT_PAYMENT_METHOD_OK': {
      if (state.status !== 'creatingSetupIntent') return state

      const { clientSecret } = event
      const {
        retailerPaymentType,
        amount,
        terminal,
        terminalMonitoringInstance,
        refundMethod,
      } = state

      exec({
        type: 'collectSetupIntentPaymentMethodEffect',
        clientSecret,
        terminal,
        terminalMonitoringInstance,
        isRetry: false,
      })

      return {
        status: 'collectingSetupIntentPaymentMethod',
        retailerPaymentType,
        amount,
        refundMethod,
        terminal,
        terminalMonitoringInstance,
        clientSecret,
      }
    }
    case 'PAYMENT_METHOD_OK': {
      if (
        state.status !== 'readingReusableCard' &&
        state.status !== 'collectingSetupIntentPaymentMethod'
      ) {
        return state
      }

      const { paymentMethodId } = event
      const { retailerPaymentType, amount } = state

      exec({ type: 'unreferencedRefundEffect', paymentMethodId, amount })

      return {
        status: 'performingUnreferencedRefund',
        retailerPaymentType,
        paymentMethodId,
        amount,
      }
    }
    case 'CARD_PRESENT_OK': {
      if (state.status !== 'refundingViaCardPresent') return state

      const { refundMethod, amount, retailerPaymentType } = state

      exec({
        type: 'persistCardPresentEffect',
        refundMethod,
        amount,
      })

      return {
        status: 'persistingCardPresentRefund',
        refundMethod,
        retailerPaymentType,
        amount,
      }
    }
    case 'RETRY': {
      // retry disabled ? do nothing
      if (!state.retryable || state.status !== 'error') return state

      const previousState = state.previousState
      const effect = getEffectForRetry(previousState)

      if (effect) exec(effect)

      return previousState
    }
    case 'ERROR': {
      return {
        status: 'error',
        errorMessage: event.errorMessage,
        retryable: event.retryable,
        dismissible: true,
        previousState: state,
      }
    }
    default: {
      // This `never` typing will let us know at compile time if we do not handle an existing event type. Neat !
      const _exhaustiveCheck: never = event
      return _exhaustiveCheck
    }
  }
}
